import { memo, useCallback, useState } from 'react'
import { Info, Layers3 } from 'lucide-react'
import { ModalShell } from '../common/ModalShell.js'

export interface CgSettingsDraft {
  expectedRevision?: string
  name: string
  slug: string
  manualOnGrid: boolean
}

interface Props {
  title: string
  maxNameLength?: number
  initial: CgSettingsDraft
  /** Chemin du fichier HTML, pour montrer ce que le renommage va deplacer. */
  htmlPath?: string | null
  /** Layer CG attribue au modele : diagnostic, pas un reglage. */
  cgLayer?: number | null
  onClose: () => void
  onSubmit: (draft: CgSettingsDraft) => Promise<void>
}

export function slugPreview(htmlPath: string, slug: string): string {
  const normalized = htmlPath.replace(/\\/g, '/')
  const folder = normalized.slice(0, normalized.lastIndexOf('/') + 1)
  return `${folder}${slug || '…'}.html`
}

export const CgTemplateSettingsDialog = memo(function CgTemplateSettingsDialog({
  title,
  maxNameLength = 255,
  initial,
  htmlPath,
  cgLayer,
  onClose,
  onSubmit,
}: Props) {
  const [draft, setDraft] = useState<CgSettingsDraft>(initial)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')

  const submit = useCallback(async () => {
    if (!draft.name.trim() || !draft.slug.trim()) return
    setSaving(true)
    setMessage('')
    try {
      await onSubmit({ ...draft, name: draft.name.trim(), slug: draft.slug.trim() })
      onClose()
    } catch (error: any) {
      setMessage(error.message ?? 'Enregistrement impossible')
    } finally {
      setSaving(false)
    }
  }, [draft, onClose, onSubmit])

  return (
    <ModalShell title={title} size="narrow" className="cg-settings-modal" onClose={onClose} closeDisabled={saving}>
      <div className="cg-settings-form">
        <label className="cg-field">
          <span>Nom</span>
          <input disabled={saving} maxLength={maxNameLength} value={draft.name} onChange={event => setDraft(current => ({ ...current, name: event.target.value }))} autoFocus />
        </label>
        <label className="cg-field">
          <span>Identifiant</span>
          <input
            disabled={saving}
            maxLength={80}
            value={draft.slug}
            onChange={event => setDraft(current => ({ ...current, slug: event.target.value }))}
            placeholder="identifiant-html"
          />
        </label>
        {htmlPath && (
          <p className="cg-settings-hint">
            Fichier : <code>{draft.slug.trim() === initial.slug ? htmlPath : slugPreview(htmlPath, draft.slug.trim())}</code>
            {draft.slug.trim() !== initial.slug && ' — le fichier sera renommé.'}
          </p>
        )}
        <label className="cg-check cg-toggle">
          <input
            disabled={saving}
            type="checkbox"
            checked={draft.manualOnGrid}
            onChange={event => setDraft(current => ({ ...current, manualOnGrid: event.target.checked }))}
          />
          <span aria-hidden="true" />
          Afficher sur la grille
        </label>
        <p className="cg-settings-hint">Proposé sous le retour antenne pour un envoi à la main.</p>
        {cgLayer !== undefined && (
          <p className="cg-settings-layer"><Layers3 size={13} aria-hidden="true" />Couche CasparCG : <strong>{cgLayer === null ? '10 (partagée par les logos)' : `${10 + cgLayer}`}</strong></p>
        )}
        {message && <p className="cg-settings-error"><Info size={13} aria-hidden="true" />{message}</p>}
      </div>
      <footer className="cg-settings-actions">
        <button type="button" className="cg-button" onClick={onClose} disabled={saving}>Annuler</button>
        <button type="button" className="cg-button is-primary" onClick={submit} disabled={saving || !draft.name.trim() || !draft.slug.trim()}>
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </footer>
    </ModalShell>
  )
})
